import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Circle, CircleMarker, Tooltip, useMap } from "react-leaflet";
import { APIProvider, Map as GMap, AdvancedMarker } from "@vis.gl/react-google-maps";
import { GMAPS_KEY, MAP_ID, GCircle, FitPoints, Dot } from "@/components/gmaps";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Map as MapIcon } from "lucide-react";

const COL = { yes: "#16a34a", no: "#94a3b8", job: "#E67E22" };

function FitBounds({ data }) {
  const map = useMap();
  useEffect(() => {
    if (!data?.job?.pickup_lat) return;
    const pts = [[data.job.pickup_lat, data.job.pickup_lng]];
    if (data.job.dropoff_lat) pts.push([data.job.dropoff_lat, data.job.dropoff_lng]);
    data.matches.forEach((m) => pts.push([m.lat, m.lng]));
    if (pts.length === 1) map.setView(pts[0], 10);
    else map.fitBounds(pts, { padding: [40, 40], maxZoom: 11 });
  }, [data, map]);
  return null;
}

function GoogleLeadArea({ data }) {
  const j = data?.job;
  const pts = [];
  if (j?.pickup_lat) pts.push({ lat: j.pickup_lat, lng: j.pickup_lng });
  if (j?.dropoff_lat) pts.push({ lat: j.dropoff_lat, lng: j.dropoff_lng });
  (data?.matches || []).forEach((m) => pts.push({ lat: m.lat, lng: m.lng }));
  return (
    <APIProvider apiKey={GMAPS_KEY}>
      <GMap mapId={MAP_ID} defaultCenter={{ lat: -28.5, lng: 134 }} defaultZoom={4} gestureHandling="cooperative"
        streetViewControl={false} mapTypeControl={false} fullscreenControl={false} style={{ width: "100%", height: "100%" }}>
        <FitPoints points={pts} />
        {data?.matches.map((m) => (
          <GCircle key={`c${m.id}`} center={{ lat: m.lat, lng: m.lng }} radius={m.max_service_radius_km * 1000}
            color={m.will_receive ? COL.yes : COL.no} fillOpacity={0.06} dashed={!m.will_receive} />
        ))}
        {data?.matches.map((m) => (
          <AdvancedMarker key={`b${m.id}`} position={{ lat: m.lat, lng: m.lng }} title={`${m.business_name}: ${m.distance_km} km away, ${m.max_service_radius_km} km radius`}>
            <Dot color={m.will_receive ? COL.yes : COL.no} size={12} />
          </AdvancedMarker>
        ))}
        {j?.pickup_lat && <AdvancedMarker position={{ lat: j.pickup_lat, lng: j.pickup_lng }} title={`Pickup: ${j.pickup_suburb}`} zIndex={10}><Dot color={COL.job} size={18} label={`Pickup: ${j.pickup_suburb}`} /></AdvancedMarker>}
        {j?.dropoff_lat && <AdvancedMarker position={{ lat: j.dropoff_lat, lng: j.dropoff_lng }} title={`Drop-off: ${j.dropoff_suburb}`}><Dot color="#334155" size={14} /></AdvancedMarker>}
      </GMap>
    </APIProvider>
  );
}

function MatchList({ matches }) {
  if (!matches.length) return <p className="text-sm text-muted-foreground" data-testid="lead-map-no-matches">No listings cover this pickup.</p>;
  return (
    <ul className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1" data-testid="lead-map-match-list">
      {matches.map((m) => (
        <li key={m.id} className="flex items-start gap-2 text-sm" data-testid={`lead-map-match-${m.id}`}>
          <span className="mt-1.5 h-2.5 w-2.5 rounded-full shrink-0" style={{ background: m.will_receive ? COL.yes : COL.no }} />
          <span className="min-w-0">
            <span className="font-semibold block truncate">{m.business_name}</span>
            <span className="text-xs text-muted-foreground">{m.distance_km} km · {m.max_service_radius_km} km radius · {m.claim_status}
              {m.lead_sent ? " · lead sent" : m.unsubscribed ? " · unsubscribed" : !m.will_receive ? " · not approved" : ""}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function LeadMap() {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/lead-map/jobs").then((r) => { setJobs(r.data); if (r.data[0]) setJobId(r.data[0].id); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!jobId) return;
    api.get(`/admin/jobs/${jobId}/lead-matches`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [jobId]);
  const j = data?.job;
  return (
    <Card className="p-4 space-y-3" data-testid="lead-map-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display font-bold flex items-center gap-2"><MapIcon className="h-4 w-4 text-kinetic" /> Lead map</h3>
        <Select value={jobId} onValueChange={setJobId}>
          <SelectTrigger className="w-full sm:w-[360px]" data-testid="lead-map-job-select"><SelectValue placeholder={jobs.length ? "Choose a job" : "No posted jobs with a map location yet"} /></SelectTrigger>
          <SelectContent>
            {jobs.map((x) => (
              <SelectItem key={x.id} value={x.id} data-testid={`lead-map-job-${x.id}`}>{x.title} · {x.pickup_suburb} → {x.dropoff_suburb} ({x.leads_sent} leads)</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {j && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="lead-map-summary">
          <Badge variant="outline">{j.pickup_suburb} {j.pickup_state} → {j.dropoff_suburb} {j.dropoff_state}</Badge>
          {data.interstate && <Badge className="bg-sky-100 text-sky-800 border-0">Interstate</Badge>}
          <span>{data.matches.length} covering · {data.matches.filter((m) => m.will_receive).length} get leads</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: COL.yes }} /> gets leads</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: COL.no }} /> covers, not sent</span>
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_280px] gap-4">
        <div className="rounded-xl overflow-hidden border border-border h-[320px] sm:h-[440px]" data-testid="lead-map" data-provider={GMAPS_KEY ? "google" : "osm"}>
          {GMAPS_KEY ? <GoogleLeadArea data={data} /> : (
          <MapContainer center={[-28.5, 134]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds data={data} />
            {data?.matches.map((m) => (
              <Circle key={`c${m.id}`} center={[m.lat, m.lng]} radius={m.max_service_radius_km * 1000}
                pathOptions={{ color: m.will_receive ? COL.yes : COL.no, fillOpacity: 0.06, weight: 1.5, dashArray: m.will_receive ? null : "4 4" }} />
            ))}
            {data?.matches.map((m) => (
              <CircleMarker key={`b${m.id}`} center={[m.lat, m.lng]} radius={6} pathOptions={{ color: "#fff", fillColor: m.will_receive ? COL.yes : COL.no, fillOpacity: 1, weight: 2 }}>
                <Tooltip>{m.business_name}: {m.distance_km} km away, {m.max_service_radius_km} km radius</Tooltip>
              </CircleMarker>
            ))}
            {j?.pickup_lat && (
              <CircleMarker center={[j.pickup_lat, j.pickup_lng]} radius={10} pathOptions={{ color: "#CF6D17", fillColor: COL.job, fillOpacity: 0.95, weight: 2 }}>
                <Tooltip permanent direction="top">Pickup: {j.pickup_suburb}</Tooltip>
              </CircleMarker>
            )}
            {j?.dropoff_lat && (
              <CircleMarker center={[j.dropoff_lat, j.dropoff_lng]} radius={7} pathOptions={{ color: "#334155", fillColor: "#334155", fillOpacity: 0.9, weight: 2 }}>
                <Tooltip>Drop-off: {j.dropoff_suburb}</Tooltip>
              </CircleMarker>
            )}
          </MapContainer>
          )}
        </div>
        {data && <MatchList matches={data.matches} />}
      </div>
    </Card>
  );
}
